
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, TextField, Typography, IconButton, InputAdornment } from '@mui/material';
import lionlogo from '../../images/mewin_logo_combined.svg';
import styles from "./styles.module.css";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from 'react-redux';
import { login, changePassword ,logout} from '../../store/slices/authSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import useListing from '../../api/userListingsocket';

// ─── Toast (success only) ────────────────────────────────────────────────────

const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M11.0038 2.29004C16.0771 2.29004 20.1914 6.40436 20.1914 11.4776C20.1914 16.5509 16.0771 20.6652 11.0038 20.6652C5.93051 20.6652 1.81619 16.5509 1.81619 11.4776C1.81619 6.40436 5.93051 2.29004 11.0038 2.29004ZM9.09587 14.4618L6.84652 12.2106C6.46331 11.8272 6.46323 11.2019 6.84652 10.8185C7.22989 10.4353 7.85795 10.4377 8.23853 10.8185L9.82431 12.4056L13.7692 8.46069C14.1526 8.07732 14.7779 8.07732 15.1612 8.46069C15.5446 8.84398 15.544 9.46987 15.1612 9.8527L10.5192 14.4947C10.1364 14.8775 9.51047 14.8781 9.12718 14.4947C9.11641 14.4839 9.10602 14.473 9.09587 14.4618Z" fill="#5FFF5F" />
    </svg>
);

const Toast = ({ toasts }) => (
    <Box sx={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center', pointerEvents: 'none', width: '90%', maxWidth: '360px' }}>
        {toasts.map((toast) => (
            <Box key={toast.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(20, 40, 20, 0.95)', border: '1px solid rgba(95, 255, 95, 0.3)', backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(95, 255, 95, 0.15)', width: '100%', animation: 'slideDown 0.3s ease forwards', '@keyframes slideDown': { from: { opacity: 0, transform: 'translateY(-10px)' }, to: { opacity: 1, transform: 'translateY(0)' } } }}>
                <CheckIcon />
                <Typography sx={{ color: '#5FFF5F', fontSize: '13px', fontWeight: 500, lineHeight: 1.4, flex: 1 }}>
                    {toast.message}
                </Typography>
            </Box>
        ))}
    </Box>
);

const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const showToast = useCallback((message, duration = 3000) => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
    }, []);
    return { toasts, showToast };
};

// ─── Inline Error Text ───────────────────────────────────────────────────────

const InlineError = ({ message }) => {
    if (!message) return null;
    return (
        <Typography sx={{ fontSize: '12px', color: '#FF3B30', marginTop: '-4px', marginBottom: '4px', paddingLeft: '2px', lineHeight: 1.4 }}>
            {message}
        </Typography>
    );
};



// ─── Password Strength Checker ───────────────────────────────────────────────

const passwordRules = [
    { key: 'uppercase', label: '1 uppercase letter', test: (v) => /[A-Z]/.test(v) },
    { key: 'lowercase', label: '1 lowercase letter', test: (v) => /[a-z]/.test(v) },
    { key: 'number',    label: '1 number',            test: (v) => /[0-9]/.test(v) },
];

const PasswordStrengthChecker = ({ value }) => {
    if (!value) return null;
    const failedRules = passwordRules.filter((rule) => !rule.test(value));
    if (failedRules.length === 0) return null;
    const failedLabels = failedRules.map((r) => r.label).join(', ');
    return (
        <Typography sx={{ fontSize: '12px', color: '#FF3B30', marginTop: '-4px', marginBottom: '4px', paddingLeft: '2px', lineHeight: 1.4 }}>
            New Password must contain at least: {failedLabels}
        </Typography>
    );
};

const validatePassword = (value) => passwordRules.every((rule) => rule.test(value));

// ─── Icons ───────────────────────────────────────────────────────────────────

const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
        <g clipPath="url(#clip0_6375_8656)">
            <path d="M11.96 12.932C10.8204 13.8007 9.4327 14.2819 7.99996 14.3053C3.33329 14.3053 0.666626 8.972 0.666626 8.972C1.49589 7.4266 2.64605 6.07641 4.03996 5.012M6.59996 3.79867C7.05885 3.69126 7.52867 3.63756 7.99996 3.63867C12.6666 3.63867 15.3333 8.972 15.3333 8.972C14.9286 9.72908 14.446 10.4418 13.8933 11.0987M9.41329 10.3853C9.23019 10.5818 9.00939 10.7394 8.76406 10.8488C8.51873 10.9581 8.25389 11.0168 7.98535 11.0216C7.71681 11.0263 7.45007 10.9769 7.20103 10.8763C6.952 10.7757 6.72577 10.626 6.53586 10.4361C6.34594 10.2462 6.19622 10.02 6.09563 9.77093C5.99504 9.5219 5.94564 9.25515 5.95038 8.98661C5.95512 8.71807 6.0139 8.45323 6.12321 8.2079C6.23252 7.96257 6.39013 7.74177 6.58663 7.55867M0.666626 1.63867L15.3333 16.3053" stroke="#C9DDF8" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <defs>
            <clipPath id="clip0_6375_8656">
                <rect width="16" height="16" fill="white" transform="translate(0 0.972168)" />
            </clipPath>
        </defs>
    </svg>
);

const PasswordField = ({ placeholder, value, onChange, show, onToggle }) => (
    <Box className={styles.textfield_box}>
        <TextField
            fullWidth
            type={show ? 'text' : 'password'}
            variant="standard"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className={styles.textfield_txt}
            InputProps={{
                disableUnderline: true,
                className: styles.textfield_txt,
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={onToggle} edge="end">
                            {show ? <VisibilityIcon /> : <EyeOffIcon />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const LoginScreen = ({ onLoginSuccess }) => {
    const [step, setStep] = useState(1);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // ─── Inline field errors ─────────────────────────────────────────────────
    const [loginError, setLoginError] = useState('');
    const [oldPasswordError, setOldPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');

    const { toasts, showToast } = useToast();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { authenticated, forcePasswordChange, loading, userId } = useSelector((s) => s.auth);

    const isChangePasswordRoute = location.pathname === '/change-password';
     
const redirectPath = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/';
    
   
    useEffect(() => {
        if (authenticated) {
            if (forcePasswordChange && !isChangePasswordRoute) {
                
                setStep(2);
            } else if (isChangePasswordRoute) {
              
                setStep(2);
            } else {
             
                onLoginSuccess?.();
                
                if (!onLoginSuccess) {
                   navigate(redirectPath, { replace: true });
                }
            }
        }
    }, [authenticated, forcePasswordChange, isChangePasswordRoute]);
    // ─────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!forcePasswordChange) return;
        window.history.pushState(null, '', window.location.href);
        const handlePopState = () => window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [forcePasswordChange]);

    // const handleLogin = async () => {
    //     setLoginError('');

    //     if (!username || !password) {
    //         setLoginError('Please enter username and password');
    //         return;
    //     }

    //     try {
    //         const resultAction = await dispatch(login({ username, password }));
       

    //         if (login.fulfilled.match(resultAction)) {
    //             if (resultAction.payload.forcePasswordChange) {
    //                 setStep(2);
    //             } else {
    //                 showToast("Login Successful! Welcome back, you're now logged in.");
      
    //                 if (!onLoginSuccess) {
    //                     setTimeout(() => navigate('/', { replace: true }), 1000);
    //                 } else {
    //                     onLoginSuccess?.();
    //                 }
    //             }
    //         } else if (login.rejected.match(resultAction)) {
    //             const code = resultAction.payload?.code;
    //             const msg = resultAction.payload?.message || "Login failed. Please check your credentials.";
    //             if (code === "INVALID_PASSWORD") {
    //                 setLoginError("Password is incorrect");
    //             } else if (code === "USER_NOT_FOUND") {
    //                 setLoginError("User not found");
    //             } else {
    //                 setLoginError(msg);
    //             }
    //         }
    //     } catch (err) {
    //         console.error(err);
    //         setLoginError("Something went wrong. Please try again later.");
    //     }
    // };


    const handleLogin = async () => {
    setLoginError('');

    const trimmedUsername = username.trim();

    if (!trimmedUsername || !password) {
        setLoginError('Please enter username and password');
        return;
    }

    try {
        const resultAction = await dispatch(login({ username: trimmedUsername, password }));

        if (login.fulfilled.match(resultAction)) {
            if (resultAction.payload.forcePasswordChange) {
                setStep(2);
            } else {
                showToast("Login Successful! Welcome back, you're now logged in.");

                if (!onLoginSuccess) {
                  setTimeout(() => navigate(redirectPath, { replace: true }), 1000);
                } else {
                    onLoginSuccess?.();
                }
            }
        } else if (login.rejected.match(resultAction)) {
            const code = resultAction.payload?.code;
            const msg = resultAction.payload?.message || "Login failed. Please check your credentials.";
            if (code === "INVALID_PASSWORD") {
                setLoginError("Password is incorrect");
            } else if (code === "USER_NOT_FOUND") {
                setLoginError("User not found");
            } else {
                setLoginError(msg);
            }
        }
    } catch (err) {
        console.error(err);
        setLoginError("Something went wrong. Please try again later.");
    }
};
    const handleChangePassword = async () => {
        const currentOldPassword = forcePasswordChange ? password : oldPassword;

        setOldPasswordError('');
        setNewPasswordError('');
        setConfirmPasswordError('');

        if (!currentOldPassword || !newPassword || !confirmPassword) {
            if (!currentOldPassword) setOldPasswordError('Please enter your old password');
            if (!confirmPassword) setConfirmPasswordError('New Password Confirmation is required');
            return;
        }

        if (newPassword === currentOldPassword) {
            setNewPasswordError('New password cannot be same as old password');
            return;
        }

        if (confirmPassword === currentOldPassword) {
            setConfirmPasswordError('Confirm password cannot be same as old password');
            return;
        }

        if (newPassword !== confirmPassword) {
            setConfirmPasswordError('New password and confirm password do not match');
            return;
        }

        try {
            const resultAction = await dispatch(
                changePassword({ userId, oldPassword: currentOldPassword, newPassword })
            );

            if (changePassword.fulfilled.match(resultAction)) {
                showToast("Password updated successfully!");
                setTimeout(() => setStep(3), 800);
                
 dispatch(logout());        
        navigate('/', { replace: true }); 
            } else if (changePassword.rejected.match(resultAction)) {
                const errorMessage = resultAction.payload?.message || "Failed to update password. Please try again.";
                const code = resultAction.payload?.code;
                if (code === "INVALID_PASSWORD" || code === "WRONG_PASSWORD") {
                    setOldPasswordError(errorMessage);
                } else {
                    setOldPasswordError(errorMessage);
                }
            }
        } catch (err) {
            console.error(err);
            setOldPasswordError("Something went wrong. Please try again later.");
        }
    };

    const handleContinue = () => {
        onLoginSuccess?.();
       navigate(redirectPath, { replace: true });
    };

    return (
        <Box className={styles.LoginScreen_container2}>
            <Toast toasts={toasts} />

            <Box className={styles.LoginScreen_container}>
                <Box>
                    <img src={lionlogo} alt="Logo" className={styles.logo} />
                </Box>

                <Box className={styles.textfield_box_container}>

                    {/* ── STEP 1: Login ── */}
                    {step === 1 && (
                        <>
                            <Box className={styles.textfield_box}>
                                {/* <TextField
                                    fullWidth
                                    variant="standard"
                                    placeholder="Username"
                                    value={username}
                                    onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
                                    className={styles.textfield_txt}
                                    InputProps={{ disableUnderline: true, className: styles.textfield_txt }}
                                /> */}

                                <TextField
    fullWidth
    variant="standard"
    placeholder="Username"
    value={username}
    onChange={(e) => { setUsername(e.target.value.trimStart()); setLoginError(''); }}
    className={styles.textfield_txt}
    InputProps={{ disableUnderline: true, className: styles.textfield_txt }}
/>
                            </Box>
                            <PasswordField
                                placeholder="Password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                                show={showLoginPassword}
                                onToggle={() => setShowLoginPassword((p) => !p)}
                            />
                            <InlineError message={loginError} />
                            <Button fullWidth variant="contained" onClick={handleLogin} disabled={loading} className={styles.button_box} sx={{ mt: '14px' }}>
                                <Typography className={styles.button_box_txt}>Login</Typography>
                            </Button>
                        </>
                    )}

                    {/* ── STEP 2: Change Password ── */}
                    {step === 2 && (
                        <>
                         <Box style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <span 
                 onClick={() => navigate('/', { replace: true })}
            style={{ position: 'absolute', top: '25px', right: '25px', cursor: 'pointer' }}
        
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none">
                    <path d="M0.586047 12.0001C0.470144 12.0001 0.356837 11.9657 0.26046 11.9014C0.164083 11.837 0.0889654 11.7455 0.0446082 11.6384C0.000250996 11.5313 -0.0113527 11.4135 0.0112649 11.2998C0.0338824 11.1861 0.0897052 11.0817 0.171672 10.9998L10.9998 0.17164C11.1097 0.0617407 11.2588 0 11.4142 0C11.5696 0 11.7186 0.0617407 11.8285 0.17164C11.9384 0.281539 12.0002 0.430594 12.0002 0.586015C12.0002 0.741436 11.9384 0.890491 11.8285 1.00039L1.00042 11.8285C0.946056 11.883 0.881462 11.9262 0.810351 11.9556C0.739239 11.9851 0.663012 12.0002 0.586047 12.0001Z" fill="white"/>
                    <path d="M11.4141 12.0001C11.3372 12.0002 11.2609 11.9851 11.1898 11.9556C11.1187 11.9262 11.0541 11.883 10.9998 11.8285L0.17164 1.00039C0.0617407 0.890491 0 0.741436 0 0.586015C0 0.430594 0.0617407 0.281539 0.17164 0.17164C0.281539 0.0617407 0.430594 0 0.586015 0C0.741436 0 0.890491 0.0617407 1.00039 0.17164L11.8285 10.9998C11.9105 11.0817 11.9663 11.1861 11.9889 11.2998C12.0115 11.4135 11.9999 11.5313 11.9556 11.6384C11.9112 11.7455 11.8361 11.837 11.7397 11.9014C11.6433 11.9657 11.53 12.0001 11.4141 12.0001Z" fill="white"/>
                </svg>
            </span>
        </Box>

                            <Typography className={styles.instruction_txt}>Please change your password</Typography>

                            <PasswordField
                                placeholder="Old Password"
                                value={forcePasswordChange ? password : oldPassword}
                                onChange={(e) => {
                                    forcePasswordChange ? setPassword(e.target.value) : setOldPassword(e.target.value);
                                    setOldPasswordError('');
                                }}
                                show={showOldPassword}
                                onToggle={() => setShowOldPassword((p) => !p)}
                            />
                            <InlineError message={oldPasswordError} />

                            <PasswordField
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(''); }}
                                show={showNewPassword}
                                onToggle={() => setShowNewPassword((p) => !p)}
                            />
                            <PasswordStrengthChecker value={newPassword} />
                            <InlineError message={newPasswordError} />

                            <PasswordField
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(''); }}
                                show={showConfirmPassword}
                                onToggle={() => setShowConfirmPassword((p) => !p)}
                            />
                            <InlineError message={confirmPasswordError} />

                            <Button fullWidth variant="contained" onClick={handleChangePassword} className={styles.button_box}>
                                
                                <Typography className={styles.button_box_txt}>Change Password</Typography>
                            </Button>
                        </>
                    )}

                    {/* ── STEP 3: Success ── */}
                    {step === 3 && (
                        <Box className={styles.successBox}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M11.0038 2.29004C16.0771 2.29004 20.1914 6.40436 20.1914 11.4776C20.1914 16.5509 16.0771 20.6652 11.0038 20.6652C5.93051 20.6652 1.81619 16.5509 1.81619 11.4776C1.81619 6.40436 5.93051 2.29004 11.0038 2.29004ZM9.09587 14.4618L6.84652 12.2106C6.46331 11.8272 6.46323 11.2019 6.84652 10.8185C7.22989 10.4353 7.85795 10.4377 8.23853 10.8185L9.82431 12.4056L13.7692 8.46069C14.1526 8.07732 14.7779 8.07732 15.1612 8.46069C15.5446 8.84398 15.544 9.46987 15.1612 9.8527L10.5192 14.4947C10.1364 14.8775 9.51047 14.8781 9.12718 14.4947C9.11641 14.4839 9.10602 14.473 9.09587 14.4618Z" fill="#5FFF5F" />
                            </svg>
                            <Typography className={styles.successText}>Password Updated Successfully</Typography>
                            <Button fullWidth variant="contained" onClick={handleContinue} className={styles.button_box}>
                                <Typography className={styles.button_box_txt}>Continue</Typography>
                            </Button>
                        </Box>
                    )}

                </Box>
            </Box>

        </Box>
    );
};

export default LoginScreen;